import React, { useMemo, useRef, useState } from 'react'
import { Tooltip } from 'flowbite-react'
import { HiPlus, HiSearch, HiX } from 'react-icons/hi'
import { dedupeKeywords, keywordKey, normalizeKeyword } from '../utils/departments'

const MAX_SUGGESTIONS = 8

/**
 * Inline editor for an AoR's issue keywords. Renders the current keywords as
 * pills (light blue, or yellow when the same keyword is defined on more than one
 * AoR across the authority — see `duplicateKeys`), a search box that suggests
 * "known" keywords from the detectable-object catalog, and an add button that
 * commits whatever is typed as an ad-hoc keyword.
 *
 * Editing is optimistic-free: every add/remove calls `onChange(nextKeywords)`
 * with the full, deduped list and the parent persists it.
 */
const KeywordEditor = ({
  keywords = [],
  knownKeywords = [],
  duplicateKeys,
  canWrite = false,
  disabled = false,
  onChange,
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const blurTimer = useRef(null)

  const currentKeys = useMemo(
    () => new Set(keywords.map((kw) => keywordKey(kw))),
    [keywords]
  )

  const trimmed = normalizeKeyword(query)
  const suggestions = useMemo(() => {
    if (!trimmed) return []
    const needle = trimmed.toLowerCase()
    return knownKeywords
      .filter(
        (kw) => !currentKeys.has(keywordKey(kw)) && kw.toLowerCase().includes(needle)
      )
      .slice(0, MAX_SUGGESTIONS)
  }, [knownKeywords, currentKeys, trimmed])

  const isDuplicate = (kw) => Boolean(duplicateKeys?.has(keywordKey(kw)))

  const commit = (value) => {
    const next = normalizeKeyword(value)
    if (!next) return
    if (currentKeys.has(next.toLowerCase())) {
      // Already present — just clear the input.
      setQuery('')
      setOpen(false)
      setActiveIndex(-1)
      return
    }
    onChange?.(dedupeKeywords([...keywords, next]))
    setQuery('')
    setOpen(false)
    setActiveIndex(-1)
  }

  const removeKeyword = (kw) => {
    const target = keywordKey(kw)
    onChange?.(keywords.filter((item) => keywordKey(item) !== target))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        commit(suggestions[activeIndex])
      } else {
        commit(query)
      }
    } else if (event.key === 'ArrowDown') {
      if (!suggestions.length) return
      event.preventDefault()
      setOpen(true)
      setActiveIndex((i) => (i + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      if (!suggestions.length) return
      event.preventDefault()
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (event.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  const pillClass = (kw) =>
    isDuplicate(kw)
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'

  const renderPill = (kw) => {
    const pill = (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${pillClass(
          kw
        )}`}
      >
        {kw}
        {canWrite && (
          <button
            type="button"
            aria-label={`Remove keyword ${kw}`}
            disabled={disabled}
            onClick={() => removeKeyword(kw)}
            className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 disabled:opacity-40 dark:hover:bg-white/10"
          >
            <HiX className="h-3 w-3" />
          </button>
        )}
      </span>
    )
    return isDuplicate(kw) ? (
      <Tooltip
        key={keywordKey(kw)}
        content="Also defined on another area of responsibility in this authority"
      >
        {pill}
      </Tooltip>
    ) : (
      <React.Fragment key={keywordKey(kw)}>{pill}</React.Fragment>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {keywords.length === 0 ? (
          <span className="text-xs italic text-gray-400 dark:text-gray-500">
            No issue keywords yet.
          </span>
        ) : (
          keywords.map((kw) => renderPill(kw))
        )}
      </div>

      {canWrite && (
        <div className="relative flex items-start gap-2">
          <div className="relative flex-1">
            <HiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              disabled={disabled}
              placeholder="Search issue keywords…"
              autoComplete="off"
              onChange={(event) => {
                setQuery(event.target.value)
                setOpen(true)
                setActiveIndex(-1)
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                blurTimer.current = setTimeout(() => setOpen(false), 120)
              }}
              onKeyDown={handleKeyDown}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 py-1.5 pl-8 pr-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            {open && suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
                {suggestions.map((kw, index) => (
                  <li key={keywordKey(kw)}>
                    <button
                      type="button"
                      // onMouseDown (not onClick) so it fires before the input blur closes the list.
                      onMouseDown={(event) => {
                        event.preventDefault()
                        if (blurTimer.current) clearTimeout(blurTimer.current)
                        commit(kw)
                      }}
                      className={`flex w-full items-center px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-200 ${
                        index === activeIndex
                          ? 'bg-primary-50 dark:bg-gray-600'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-600/60'
                      }`}
                    >
                      {kw}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            aria-label="Add issue keyword"
            title="Add this text as a keyword"
            disabled={disabled || !trimmed}
            onClick={() => commit(query)}
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-primary-600 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            <HiPlus className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

export default KeywordEditor
